import { useEffect, useReducer, useRef } from "react";
import {
  DEMO_DETAIL_TABS,
  type DemoDetailTab,
  type DemoInteractionState,
} from "./types";

const STORAGE_PREFIX = "demo:roadmap-validator";

function toStorageKey(nicheSlug: string): string {
  return `${STORAGE_PREFIX}:${nicheSlug}`;
}

export function loadDemoInteractionState(
  nicheSlug: string,
): DemoInteractionState | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(toStorageKey(nicheSlug));
    if (!rawValue) {
      return null;
    }

    const parsed: unknown = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const candidate = parsed as Partial<DemoInteractionState>;
    if (candidate.nicheSlug !== nicheSlug) {
      return null;
    }

    return {
      nicheSlug,
      savedPainPointIds: Array.isArray(candidate.savedPainPointIds)
        ? candidate.savedPainPointIds.filter(
            (value): value is string => typeof value === "string",
          )
        : [],
      selectedIdeaId:
        typeof candidate.selectedIdeaId === "string"
          ? candidate.selectedIdeaId
          : undefined,
      lastActiveTab:
        typeof candidate.lastActiveTab === "string"
          ? (candidate.lastActiveTab as DemoDetailTab)
          : undefined,
      momTestAnswers:
        candidate.momTestAnswers && typeof candidate.momTestAnswers === "object"
          ? candidate.momTestAnswers
          : undefined,
    };
  } catch {
    return null;
  }
}

export function saveDemoInteractionState(state: DemoInteractionState): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      toStorageKey(state.nicheSlug),
      JSON.stringify(state),
    );
  } catch {
    // Intentionally ignore storage write errors in demo mode.
  }
}

export function clearDemoInteractionState(nicheSlug: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(toStorageKey(nicheSlug));
  } catch {
    // Intentionally ignore storage remove errors in demo mode.
  }
}

type DemoPersistenceAction =
  | { type: "hydrate"; state: DemoInteractionState }
  | { type: "togglePainPoint"; painPointId: string }
  | { type: "clearSavedPainPoints" }
  | { type: "setActiveTab"; tab: DemoDetailTab }
  | { type: "setSelectedIdea"; ideaId: string }
  | { type: "setMomTestAnswer"; questionId: string; answer: string };

function createDefaultState(nicheSlug: string): DemoInteractionState {
  return {
    nicheSlug,
    savedPainPointIds: [],
    lastActiveTab: DEMO_DETAIL_TABS.OVERVIEW,
  };
}

function reducer(
  state: DemoInteractionState,
  action: DemoPersistenceAction,
): DemoInteractionState {
  switch (action.type) {
    case "hydrate":
      return action.state;
    case "togglePainPoint": {
      const alreadySaved = state.savedPainPointIds.includes(action.painPointId);
      return {
        ...state,
        savedPainPointIds: alreadySaved
          ? state.savedPainPointIds.filter((id) => id !== action.painPointId)
          : [...state.savedPainPointIds, action.painPointId],
      };
    }
    case "setActiveTab":
      return {
        ...state,
        lastActiveTab: action.tab,
      };
    case "clearSavedPainPoints":
      return {
        ...state,
        savedPainPointIds: [],
      };
    case "setSelectedIdea":
      return {
        ...state,
        selectedIdeaId: action.ideaId,
      };
    case "setMomTestAnswer":
      return {
        ...state,
        momTestAnswers: {
          ...(state.momTestAnswers ?? {}),
          [action.questionId]: action.answer,
        },
      };
    default:
      return state;
  }
}

export function useDemoPersistence(nicheSlug: string) {
  const [state, dispatch] = useReducer(reducer, nicheSlug, createDefaultState);
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    hasHydratedRef.current = false;
    const stored = loadDemoInteractionState(nicheSlug);
    if (stored) {
      dispatch({ type: "hydrate", state: stored });
      hasHydratedRef.current = true;
      return;
    }

    dispatch({ type: "hydrate", state: createDefaultState(nicheSlug) });
    hasHydratedRef.current = true;
  }, [nicheSlug]);

  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    saveDemoInteractionState(state);
  }, [state]);

  return {
    interactionState: state,
    togglePainPointSaved: (painPointId: string) => {
      dispatch({ type: "togglePainPoint", painPointId });
    },
    setActiveTab: (tab: DemoDetailTab) => {
      dispatch({ type: "setActiveTab", tab });
    },
    setSelectedIdea: (ideaId: string) => {
      dispatch({ type: "setSelectedIdea", ideaId });
    },
    clearSavedPainPoints: () => {
      dispatch({ type: "clearSavedPainPoints" });
    },
    setMomTestAnswer: (questionId: string, answer: string) => {
      dispatch({ type: "setMomTestAnswer", questionId, answer });
    },
  };
}
